// src/pages/admin/ServiceDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getService, deleteService, updateService } from '../../utils/api';
import AdminSidebar from '../../components/admin/AdminSidebar';
import Loading from '../../components/common/Loading';
import toast from 'react-hot-toast';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaTrash, 
  FaListAlt,
  FaInfoCircle,
  FaImages,
  FaChartBar,
  FaCogs,
  FaCheckCircle,
  FaEye,
  FaImage,
  FaTag,
  FaCalendarAlt,
  FaFileAlt
} from 'react-icons/fa';

const AdminServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    category: '',
    title: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    loadService();
  }, [id]);

  const loadService = async () => {
    try {
      setLoading(true);
      const data = await getService(id);
      setService(data);
      setEditData({
        category: data.category || '',
        title: data.title || '',
        description: data.description || '',
        is_active: data.is_active !== undefined ? data.is_active : true
      });
    } catch (error) {
      console.error('Error loading service:', error);
      toast.error('Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      try {
        await deleteService(id);
        toast.success('Service deleted successfully');
        navigate('/admin/services');
      } catch (error) {
        console.error('Error deleting service:', error);
        toast.error('Failed to delete service');
      }
    }
  };

  const handleToggleActive = async () => {
    try {
      const newStatus = !service.is_active;
      await updateService(id, { is_active: newStatus });
      setService(prev => ({ ...prev, is_active: newStatus }));
      setEditData(prev => ({ ...prev, is_active: newStatus }));
      toast.success(`Service ${newStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling service status:', error);
      toast.error('Failed to update service status');
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Save changes
      handleSaveEdit();
    } else {
      setEditMode(true);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await updateService(id, editData);
      setService(prev => ({ ...prev, ...editData }));
      toast.success('Service updated successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      category: service.category || '',
      title: service.title || '',
      description: service.description || '',
      is_active: service.is_active !== undefined ? service.is_active : true
    });
    setEditMode(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <Loading fullScreen message="Loading service details..." />
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Service Not Found</h2>
            <Link to="/admin/services" className="text-blue-600 hover:text-blue-800 inline-flex items-center">
              <FaArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      <div className="flex-1 p-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Service Details</h1>
              <p className="text-gray-600 mt-2">
                View and manage details for: <span className="font-semibold text-gray-800">{service.title}</span>
              </p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin/services"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft className="h-4 w-4 mr-2" />
                Back to Services
              </Link>
              <button
                onClick={handleEditToggle}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  editMode 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FaEdit className="h-4 w-4 mr-2" />
                {editMode ? 'Save Changes' : 'Edit Service'}
              </button>
              {editMode && (
                <button
                  onClick={handleCancelEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg">
          {/* Service Info Card */}
          <div className="p-6 border-b">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Service Image */}
              <div className="lg:w-1/3">
                {service.image_url ? (
                  <div className="relative">
                    <img 
                      src={service.image_url} 
                      alt={service.title}
                      className="w-full h-64 object-cover rounded-lg shadow"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/default-service.jpg';
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
                      <FaImage className="inline mr-1 h-3 w-3" />
                      Image
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <FaImage className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No image available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Service Details */}
              <div className="lg:w-2/3">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData({...editData, title: e.target.value})}
                        className="text-2xl font-bold text-gray-800 border-b border-blue-500 focus:outline-none"
                      />
                    ) : (
                      <h2 className="text-2xl font-bold text-gray-800">{service.title}</h2>
                    )}
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      service.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <button
                    onClick={handleToggleActive}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      service.is_active
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    {service.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>

                {/* Category */}
                <div className="mb-4">
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <FaTag className="h-4 w-4 mr-2" />
                    Category
                  </div>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.category}
                      onChange={(e) => setEditData({...editData, category: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <div className="font-semibold text-gray-800">{service.category || 'Uncategorized'}</div>
                  )}
                </div>

                {/* Description */}
                <div className="mb-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <FaFileAlt className="h-4 w-4 mr-2" />
                    Description
                  </div>
                  {editMode ? (
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({...editData, description: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="4"
                    />
                  ) : (
                    <div className="prose max-w-none">
                      <p className="text-gray-600 whitespace-pre-line">{service.description || 'No description provided.'}</p>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Service ID</div>
                    <div className="font-medium text-gray-800 truncate">{service.id.substring(0, 8)}...</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Created</div>
                    <div className="font-medium text-gray-800">
                      {service.created_at ? formatDate(service.created_at) : 'N/A'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Updated</div>
                    <div className="font-medium text-gray-800">
                      {service.updated_at ? formatDate(service.updated_at) : 'N/A'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="font-medium text-gray-800">
                      <span className={`inline-flex items-center ${service.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        <FaCheckCircle className="h-3 w-3 mr-1" />
                        {service.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="border-b">
            <div className="flex space-x-1 px-6">
              {['overview', 'details', 'gallery', 'analytics'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'overview' && <FaInfoCircle className="inline mr-2 h-4 w-4" />}
                  {tab === 'details' && <FaCogs className="inline mr-2 h-4 w-4" />}
                  {tab === 'gallery' && <FaImages className="inline mr-2 h-4 w-4" />}
                  {tab === 'analytics' && <FaChartBar className="inline mr-2 h-4 w-4" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Service Details */}
                {service.details && Object.keys(service.details).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <FaListAlt className="h-5 w-5 mr-2" />
                      Service Details
                    </h3>
                    <div className="space-y-3">
                      {service.details.services && Array.isArray(service.details.services) && service.details.services.length > 0 ? (
                        <ul className="space-y-2">
                          {service.details.services.map((item, index) => (
                            <li key={index} className="flex items-start">
                              <FaCheckCircle className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 italic">No specific service items defined.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/services/${service.id}`}
                      target="_blank"
                      className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <FaEye className="h-4 w-4 mr-2" />
                      View Public Page
                    </Link>
                    <Link
                      to={`/admin/services?edit=${service.id}`}
                      className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <FaEdit className="h-4 w-4 mr-2" />
                      Edit Full Details
                    </Link>
                    <button
                      onClick={handleToggleActive}
                      className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                        service.is_active
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      <FaCheckCircle className="h-4 w-4 mr-2" />
                      {service.is_active ? 'Deactivate Service' : 'Activate Service'}
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                  <h3 className="text-lg font-bold text-red-800 mb-2">Danger Zone</h3>
                  <p className="text-red-600 mb-4">These actions are irreversible. Please be cautious.</p>
                  <button
                    onClick={handleDelete}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4 mr-2" />
                    Delete Service
                  </button>
                </div>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Raw Service Data */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Raw Service Data</h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <pre className="p-4 text-sm text-gray-700 overflow-x-auto">
                      {JSON.stringify(service, null, 2)}
                    </pre>
                  </div>
                </div>

                {/* API Information */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">API Information</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Service Endpoint</div>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        GET /api/services/{service.id}
                      </code>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Update Endpoint</div>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        PUT /api/services/{service.id}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="space-y-6">
                {/* Service Image */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Service Image</h3>
                  {service.image_url ? (
                    <div className="relative">
                      <img 
                        src={service.image_url} 
                        alt={service.title}
                        className="w-full max-w-md h-auto rounded-lg shadow"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/default-service.jpg';
                        }}
                      />
                      <div className="mt-4">
                        <div className="text-sm text-gray-500 mb-1">Image URL</div>
                        <div className="bg-gray-100 p-3 rounded-lg overflow-x-auto">
                          <code className="text-sm text-gray-700 break-all">{service.image_url}</code>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <FaImage className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-800 mb-2">No Image Available</h4>
                      <p className="text-gray-600 mb-4">This service doesn't have an associated image.</p>
                      <Link
                        to={`/admin/services?edit=${service.id}`}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaEdit className="h-4 w-4 mr-2" />
                        Add Image
                      </Link>
                    </div>
                  )}
                </div>

                {/* Additional Images Section */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Additional Media</h3>
                  <p className="text-gray-600">Service media gallery features coming soon...</p>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="text-center py-12">
                <div className="inline-block p-8 bg-gray-50 rounded-lg">
                  <FaChartBar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Analytics Coming Soon</h3>
                  <p className="text-gray-600">Service analytics and performance metrics are currently in development.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServiceDetail;